/* eslint-disable no-plusplus */
import { Content } from "@iventis/translations/content/typed-content";
import { Node } from "@iventis/domain-model/model/node";
import { createInitialState, findParentNodeByType, useTreeBrowserInitialise } from "@iventis/tree-browser";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormWizardTemplate, Stage } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { useDelayedConstant } from "@iventis/utilities";
import { TreeBrowserDomainNodeContext as TreeBrowserContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { useQuery } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { createLayersImportNotification, useNotificationContext } from "@iventis/notifications";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { RootDispatch, RootState } from "./state/root.store";
import { sidebar } from "./state/sidebar.slice";
import { libraryNodeServices } from "./services/library-api-functions";
import { getSort } from "./plan-library/library-get-initial-values";
import { EmbeddedLibraryComponent, EmbeddedLibraryModal, StyledLibraryContainer } from "./plan-library/embedded-library";
import { getSpatialLibraries } from "./services/libraries";
import { importLayersAndFolders, getImportLayersName } from "./import-layer-dialog-helpers";

export const ImportLayersDialog: React.FC<{
    originLayerIds: string[];
}> = ({ originLayerIds: originLayerIdsAlreadyInMap }) => {
    const [saving, setSaving] = useState(false);
    const dispatch: RootDispatch = useDispatch<RootDispatch>();
    const { mapId } = useSelector((rootState: RootState) => rootState.mapReducer.mapModule);
    const { showImportLayer } = useSelector((rootState: RootState) => rootState.sidebarReducer);
    const { userId, projectId } = useSelector((s: RootState) => ({ userId: s.auth.user.userId, projectId: s.auth.user.projectId }));

    const { data: libraryNodes } = useQuery(["libraryNodes"], async () => getSpatialLibraries(), {
        refetchOnWindowFocus: false,
        initialData: [],
    });

    const [treeBrowserState, treeBrowserContext] = useTreeBrowserInitialise(
        createInitialState<Node>({ sort: getSort(userId), treeId: mapId }),
        libraryNodeServices
    );
    const selectedNodeLength = treeBrowserState?.selectedNodeIds?.length;

    // We can only import from shared library -> personal library
    // This grabs the library as it will load from the current map's library first
    const { value: canSwitchLibrary } = useDelayedConstant(
        () => treeBrowserState?.mainNodeId === libraryNodes.find((node: Node) => node.type === NodeType.PersonalMappingLibrary)?.id,
        !treeBrowserState.isLoadingTree
    );

    const translate = useIventisTranslate();

    const { createNotification } = useNotificationContext();

    const closeModal = () => {
        setSaving(false);
        dispatch(sidebar.actions.showImportLayer(false));
    };

    const onModalConfirm = async () => {
        setSaving(true);
        const importId = uuid();
        const layerNodesToImport = await importLayersAndFolders(treeBrowserState.tree, treeBrowserState.selectedNodeIds, { mapId, originLayerIdsAlreadyInMap }, importId);
        incrementMonitoringCount({ name: MetricName.IMPORT_LAYER, resourceId: mapId, quantity: layerNodesToImport.length });
        if (layerNodesToImport.length > 0) {
            createNotification(
                createLayersImportNotification(userId, projectId, importId, {
                    mapId,
                    name: getImportLayersName(layerNodesToImport),
                })
            );
        }
        setSaving(false);
        closeModal();
    };

    const stage: Stage = {
        primaryButtonCallback: onModalConfirm,
        primaryButtonText: translate(Content.map.import_layer.confirm),
        secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: closeModal }],
        isValid: selectedNodeLength > 0,
    };

    const canSelectNode = (node: Node) => {
        // Only can import layers, folders and maps
        if (![NodeType.Layer, NodeType.Folder, NodeType.Map].includes(node.type)) {
            return { disabled: true, tooltip: translate(Content.map.import_layers_tooltip[node.type]) };
        }

        // If the node is already in the map you can't import it
        if (originLayerIdsAlreadyInMap.includes(node.sourceId)) {
            return { disabled: true, tooltip: translate(Content.map.import_layers_tooltip[node.type]) };
        }

        // If the node is a folder ensure that it is has parent of a map and not in the spatial library
        if (node.type === NodeType.Folder && findParentNodeByType(treeBrowserState.tree, NodeType.Map, node, false) == null) {
            return { disabled: true, tooltip: translate(Content.map7.importLayers.folderOutsideMap) };
        }

        return { disabled: false, tooltip: undefined };
    };

    return (
        <>
            <EmbeddedLibraryModal open={showImportLayer} close={closeModal} testid="import-layers-modal">
                <FormWizardTemplate stages={[stage]} currentStage={0} isSubmitting={saving} title={translate(Content.map.import_layer.title)}>
                    <TreeBrowserContext.Provider value={treeBrowserContext}>
                        <StyledLibraryContainer>
                            {treeBrowserContext != null && (
                                <EmbeddedLibraryComponent
                                    canSwitchLibrary={canSwitchLibrary}
                                    onLibrarySelectionChange={(node) => {
                                        treeBrowserContext.setTree(node);
                                    }}
                                    libraries={libraryNodes}
                                    userId={userId}
                                    canSelectNode={canSelectNode}
                                />
                            )}
                        </StyledLibraryContainer>
                    </TreeBrowserContext.Provider>
                </FormWizardTemplate>
            </EmbeddedLibraryModal>
        </>
    );
};
